// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Demo-only cosmetics: how a site's name is *shown*, when the name OMS gives
// it reads badly on screen.
//
// ---------------------------------------------------------------------------
// This is NOT a contract, and NOT a place for real data
// ---------------------------------------------------------------------------
// OMS returns two sites to the demo user — "GA-10" (`ga-10`) and "Mock GA-10"
// (`mock-ga10`) — which read as one site duplicated rather than a fleet of
// two. Renaming the site upstream was out of scope, so the front end renames
// it on the way to the screen and nowhere else.
//
// `site_key` is a FUNCTIONAL identifier, not a label: the fleet map resolves
// coordinates from it (components/fleet-map/siteCoordinates.ts). So
// `displaySiteKey` is for rendering only — never feed its result to a hook, a
// query key, or a service call.
//
// To remove: delete the entry below. If the map empties out, delete this
// module with it. See docs/design/hardcoded-frontend-data.md
// ("Demo-only display overrides") for the real fix.
// ---------------------------------------------------------------------------

interface SiteDisplayOverride {
  /** Replaces the site's OMS `label` everywhere it is displayed. */
  readonly label: string;
  /** Replaces the site's `site_key` everywhere it is *displayed*. */
  readonly key: string;
}

/**
 * site_key → what to show instead. Keys are matched case-insensitively (site
 * keys are not consistently cased across OMS and the twin — the same reason
 * `lookupCoordinate` normalizes).
 */
const SITE_DISPLAY_OVERRIDES: Readonly<Record<string, SiteDisplayOverride>> = {
  'mock-ga10': { label: 'Prime Edge Data Centers GA-11', key: 'ga-11' },
};

const lookupOverride = (siteKey: string | null | undefined) =>
  siteKey ? SITE_DISPLAY_OVERRIDES[siteKey.trim().toLowerCase()] : undefined;

/** The label to display for a site, overridden for the demo where needed. */
export function displaySiteLabel(siteKey: string | null | undefined, label: string): string {
  return lookupOverride(siteKey)?.label ?? label;
}

/**
 * The site key to *display*. Display only — the real `site_key` still goes to
 * every API and to the map's coordinate lookup.
 */
export function displaySiteKey(siteKey: string): string {
  return lookupOverride(siteKey)?.key ?? siteKey;
}
